import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PopupModal } from "../../components/Modal";
import { updateContract } from "../../helpers/api/crm";
import type { Customer360Snapshot, UpdateContractDto } from "../../types/crm";

interface Props {
  customerId: string;
  contract: Customer360Snapshot["contract"];
  onClose: () => void;
}

function LabeledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-content-tertiary">{label}</label>
      {children}
    </div>
  );
}

/**
 * Inline edit modal for contract fields. Per user directive 2026-04-23,
 * only `commissionRate` and `cancellationClause` are editable:
 *   - services is implied by user.userType (pre-filled at signup, never edited separately)
 *   - tier is not a concept in the current billing model
 *   - termMonths is not formally tracked
 *
 * Commission rate writes to `client_billing_config.commissionRate` on the
 * backend — the source of truth that /billing and commission-charge read.
 */
export function EditContractModal({ customerId, contract, onClose }: Props) {
  const queryClient = useQueryClient();

  const [commissionRatePct, setCommissionRatePct] = useState<string>(
    contract.commissionRate !== null
      ? String(Math.round(contract.commissionRate * 100 * 100) / 100)
      : "",
  );
  const [cancellationClause, setCancellationClause] = useState<string>(
    contract.cancellationClause ?? "",
  );

  const mutation = useMutation({
    mutationFn: (patch: UpdateContractDto) => updateContract(customerId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["crm", "customer360", customerId],
      });
      // Commission rate changes also affect the dashboard list.
      queryClient.invalidateQueries({ queryKey: ["crm", "customers"] });
      onClose();
    },
  });

  const handleSave = () => {
    const patch: UpdateContractDto = {};

    const parsedRate = parseFloat(commissionRatePct);
    const existingRatePct =
      contract.commissionRate !== null
        ? Math.round(contract.commissionRate * 100 * 100) / 100
        : null;
    if (commissionRatePct === "") {
      if (existingRatePct !== null) patch.commissionRate = null;
    } else if (!isNaN(parsedRate) && parsedRate !== existingRatePct) {
      patch.commissionRate = parsedRate / 100;
    }

    if (cancellationClause !== (contract.cancellationClause ?? "")) {
      patch.cancellationClause = cancellationClause || null;
    }

    // Nothing to save? Just close.
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(patch);
  };

  const inputClass =
    "w-full px-3 py-2 text-sm rounded-md border border-border-secondary bg-surface-primary text-content-primary focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 placeholder:text-content-tertiary";
  const disabledInputClass =
    "w-full px-3 py-2 text-sm rounded-md border border-border-secondary bg-surface-secondary text-content-tertiary cursor-not-allowed";

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="w-full max-w-lg mx-auto"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-content-primary">
          Edit Contract
        </h3>
        <button
          onClick={onClose}
          className="text-content-tertiary hover:text-content-primary transition-colors"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-5 h-5"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {mutation.isError && (
        <div className="px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          Failed to save changes. Please try again.
        </div>
      )}

      <div className="flex flex-col gap-4">
        {/* Signed date — read-only */}
        <LabeledField label="Signed date (read-only)">
          <div className="relative">
            <input
              disabled
              value={
                contract.signedDate
                  ? new Date(contract.signedDate).toLocaleDateString("en-US", {
                      timeZone: "America/New_York",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "Not yet signed"
              }
              className={disabledInputClass}
            />
            <span
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-content-tertiary bg-surface-secondary border border-border-primary rounded px-1.5 py-0.5 cursor-help"
              title="Stripe-sourced, not editable"
            >
              Read-only
            </span>
          </div>
          <p className="text-[11px] text-content-tertiary">
            Stripe-sourced — cannot be edited directly.
          </p>
        </LabeledField>

        {/* Commission rate — writes to client_billing_config */}
        <LabeledField label="Commission rate (%)">
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={commissionRatePct}
              onChange={(e) => setCommissionRatePct(e.target.value)}
              placeholder="e.g. 13"
              className={inputClass + " pr-8"}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-content-tertiary text-sm">
              %
            </span>
          </div>
          <p className="text-[11px] text-content-tertiary">
            Saves to the billing config — same value used by /billing and
            commission charges.
          </p>
        </LabeledField>

        {/* Cancellation clause */}
        <LabeledField label="Cancellation clause">
          <textarea
            rows={3}
            value={cancellationClause}
            onChange={(e) => setCancellationClause(e.target.value)}
            placeholder="Describe any cancellation terms..."
            className={inputClass + " resize-none"}
          />
        </LabeledField>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          onClick={onClose}
          disabled={mutation.isPending}
          className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary rounded-md border border-border-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {mutation.isPending ? "Saving..." : "Save changes"}
        </button>
      </div>
    </PopupModal>
  );
}
